import { useState, useEffect } from 'react';
import { 
  X, 
  ChevronRight, 
  ChevronLeft,
  LayoutDashboard,
  TrendingUp,
  Bell,
  Play,
  Tv,
  MessageCircle,
  Sparkles,
  CheckCircle2
} from 'lucide-react';
import { Button } from '@/components/ui/button';

const TOUR_STEPS = [
  {
    id: 'welcome',
    title: 'Welcome to TradeForge!',
    description: 'Your AI-powered trading command center. Let me show you around in 60 seconds.',
    icon: Sparkles,
    color: 'from-cyan-500 to-blue-500',
    position: 'center'
  },
  {
    id: 'dashboard',
    title: 'Real-Time Dashboard',
    description: 'Monitor live market prices, your trading stats, and market session status all in one place.',
    icon: LayoutDashboard,
    color: 'from-emerald-500 to-teal-500',
    highlight: '[data-testid="stat-active-signals"]',
    position: 'bottom'
  },
  {
    id: 'signals',
    title: 'AI Trading Signals',
    description: 'Get precise entry zones, stop losses, and take profit levels. Each signal includes confidence ratings and risk-reward ratios.',
    icon: TrendingUp,
    color: 'from-purple-500 to-pink-500',
    highlight: '[data-testid="view-all-signals-btn"]',
    position: 'left'
  },
  {
    id: 'trading',
    title: 'Paper Trading',
    description: 'Practice your strategies risk-free with our virtual trading system. Start with $100,000 demo balance.',
    icon: Play,
    color: 'from-orange-500 to-red-500',
    highlight: '[data-testid="nav-demo-trading"]',
    position: 'right'
  },
  {
    id: 'news',
    title: 'Live Financial News',
    description: '4-way live stream grid with Bloomberg, CNBC, and more. Stay informed with real-time market coverage.',
    icon: Tv,
    color: 'from-red-500 to-pink-500',
    highlight: '[data-testid="channel-card-bloomberg"]',
    position: 'top'
  },
  {
    id: 'notifications',
    title: 'Instant Alerts',
    description: 'Never miss a signal. Get notifications via in-app, email, and Telegram (Pro feature).',
    icon: Bell,
    color: 'from-yellow-500 to-orange-500',
    highlight: '[data-testid="nav-notifications"]',
    position: 'right'
  },
  {
    id: 'chatbot',
    title: 'AI Support Assistant',
    description: 'Have questions? Our AI assistant is available 24/7 to help with signals, features, and trading basics.',
    icon: MessageCircle,
    color: 'from-cyan-500 to-blue-500',
    highlight: '[data-testid="chatbot-trigger"]',
    position: 'left'
  },
  {
    id: 'complete',
    title: "You're All Set!",
    description: "Start exploring TradeForge. Check today's signals and begin your trading journey.",
    icon: CheckCircle2,
    color: 'from-green-500 to-emerald-500',
    position: 'center'
  }
];

export default function WelcomeTour({ onComplete }) {
  const [currentStep, setCurrentStep] = useState(0);
  const [isVisible, setIsVisible] = useState(true);

  const step = TOUR_STEPS[currentStep];
  const Icon = step.icon;
  const isFirstStep = currentStep === 0;
  const isLastStep = currentStep === TOUR_STEPS.length - 1;
  const progress = ((currentStep + 1) / TOUR_STEPS.length) * 100;

  useEffect(() => {
    // Highlight current element
    if (step.highlight) {
      const el = document.querySelector(step.highlight);
      if (el) {
        el.classList.add('tour-highlight');
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }

    return () => {
      // Remove highlight
      document.querySelectorAll('.tour-highlight').forEach(el => {
        el.classList.remove('tour-highlight');
      });
    };
  }, [currentStep, step.highlight]);

  const handleNext = () => {
    if (isLastStep) {
      handleComplete();
    } else {
      setCurrentStep(prev => prev + 1);
    }
  };

  const handlePrev = () => {
    if (!isFirstStep) {
      setCurrentStep(prev => prev - 1);
    }
  };

  const handleComplete = () => {
    localStorage.setItem('tour_completed', 'true');
    setIsVisible(false);
    onComplete?.();
  };

  const handleSkip = () => {
    localStorage.setItem('tour_completed', 'true');
    setIsVisible(false);
    onComplete?.();
  };

  if (!isVisible) return null;

  return (
    <>
      {/* Overlay */}
      <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[100]" />

      {/* Tour Card */}
      <div className="fixed inset-0 flex items-center justify-center z-[101] p-4">
        <div className="relative w-full max-w-lg animate-in zoom-in-95 duration-300">
          {/* Glow effect */}
          <div className={`absolute inset-0 bg-gradient-to-r ${step.color} rounded-2xl blur-xl opacity-30`} />
          
          {/* Card */}
          <div className="relative bg-[#0D131C] border border-[#1E293B] rounded-2xl overflow-hidden shadow-2xl">
            {/* Progress bar */}
            <div className="h-1 bg-[#1E293B]">
              <div 
                className={`h-full bg-gradient-to-r ${step.color} transition-all duration-500`}
                style={{ width: `${progress}%` }}
              />
            </div>

            {/* Close button */}
            <button
              onClick={handleSkip}
              className="absolute top-4 right-4 w-8 h-8 rounded-full bg-[#1E293B] hover:bg-[#2D3748] flex items-center justify-center text-slate-400 hover:text-white transition-colors z-10"
            >
              <X className="w-4 h-4" />
            </button>

            {/* Content */}
            <div className="p-8">
              {/* Icon */}
              <div className={`w-20 h-20 rounded-2xl bg-gradient-to-br ${step.color} flex items-center justify-center mb-6 mx-auto shadow-lg`}>
                <Icon className="w-10 h-10 text-white" />
              </div>

              {/* Step indicator */}
              <div className="flex justify-center gap-1.5 mb-6">
                {TOUR_STEPS.map((_, idx) => (
                  <div
                    key={idx}
                    className={`h-1.5 rounded-full transition-all duration-300 ${
                      idx === currentStep 
                        ? 'w-8 bg-cyan-400' 
                        : idx < currentStep 
                          ? 'w-1.5 bg-cyan-400/50' 
                          : 'w-1.5 bg-[#1E293B]'
                    }`}
                  />
                ))}
              </div>

              {/* Text */}
              <h2 className="text-2xl font-bold text-white text-center mb-3 font-['Manrope']">
                {step.title}
              </h2>
              <p className="text-slate-400 text-center leading-relaxed mb-8">
                {step.description}
              </p>

              {/* Actions */}
              <div className="flex items-center justify-between">
                <Button
                  variant="ghost"
                  onClick={handlePrev}
                  disabled={isFirstStep}
                  className="text-slate-400 hover:text-white disabled:opacity-0"
                >
                  <ChevronLeft className="w-4 h-4 mr-1" />
                  Back
                </Button>

                <span className="text-xs text-slate-500 font-mono">
                  {currentStep + 1} / {TOUR_STEPS.length}
                </span>

                <Button
                  onClick={handleNext}
                  className={`bg-gradient-to-r ${step.color} hover:opacity-90 text-white font-semibold px-6`}
                >
                  {isLastStep ? (
                    <>
                      Get Started
                      <CheckCircle2 className="w-4 h-4 ml-2" />
                    </>
                  ) : (
                    <>
                      Next
                      <ChevronRight className="w-4 h-4 ml-1" />
                    </>
                  )}
                </Button>
              </div>

              {/* Skip link */}
              {!isLastStep && (
                <button
                  onClick={handleSkip}
                  className="w-full text-center text-xs text-slate-500 hover:text-slate-400 mt-4"
                >
                  Skip tour
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* CSS for highlight effect */}
      <style>{`
        .tour-highlight {
          position: relative;
          z-index: 102;
          box-shadow: 0 0 0 4px rgba(0, 216, 255, 0.5), 0 0 30px rgba(0, 216, 255, 0.3);
          border-radius: 8px;
          animation: tour-pulse 2s ease-in-out infinite;
        }
        
        @keyframes tour-pulse {
          0%, 100% { box-shadow: 0 0 0 4px rgba(0, 216, 255, 0.5), 0 0 30px rgba(0, 216, 255, 0.3); }
          50% { box-shadow: 0 0 0 8px rgba(0, 216, 255, 0.3), 0 0 50px rgba(0, 216, 255, 0.5); }
        }
      `}</style>
    </>
  );
}
